<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchFeeset,
  createFeeset,
  updateFeeset,
  fetchFeesetFees,
  fetchFee,
  createFee,
  updateFee,
  deleteFee,
} from '@apis/admin/pg'
import { fetchAssets } from '@apis/admin/asset'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem },
  props: {
    feeset_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      assets: [],
      feeset_fees: [],
      fee_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadFeeset(), this.loadAssets()])
        .then(() => this.loadFeesetFees())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadFeeset() {
      let { feeset_info } = await fetchFeeset(this.feeset_seq)
      this.params = feeset_info = defaults(feeset_info, {
        seq: null,
        title: '',
        fee_rate: '0',
        regdate: null,
      })

      this.info = feeset_info
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    async loadFeesetFees() {
      if (!this.info) return []

      const { data } = await fetchFeesetFees(this.info.seq)

      this.feeset_fees = data
    },
    async loadFee(document_seq) {
      const { data } = await fetchFee(this.info.seq, document_seq)

      this.fee_params = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateFeeset : createFeeset
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_feeset_modify')
        : this.$t('admin.msg_feeset_enroll')

      const params = { ...this.params }

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddFee() {
      this.feeset_fees.push({
        feeset_seq: this.info.seq,
        asset_seq: undefined,
        fee_rate: '0',
      })
    },
    async handleDeleteFee(idx) {
      this.loading = true
      try {
        if (this.feeset_fees[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteFee(this.feeset_fees[idx].seq)
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.feeset_fees.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitFee(idx) {
      this.loading = true
      const params = this.feeset_fees[idx]
      const createOrUpdate = params.seq ? updateFee : createFee
      createOrUpdate(params)
        .then(({ seq }) => fetchFee(seq))
        .then(({ fee_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.feeset_fees, idx, { ...fee_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('pg_feeset_info.title') }}</div></th
              >
              <td
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
              <th scope="row"
                ><div>{{
                  $t('pg_feeset_info.electronic_property_fee')
                }}</div></th
              >
              <td
                ><input
                  v-model="params.fee_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.tRight]"
                />
                %</td
              >
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('pg_feeset_info.fee_by_asset')"
      name="FEE"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">
              <div>
                {{ $t('pg_feeset_info.settlement_assets') }}
              </div>
            </th>
            <th scope="col">
              <div :style="[$style.nowrap]">
                {{ $t('pg_feeset_info.commission_rate') }}
              </div>
            </th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddFee"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(feeset_fee, idx) in feeset_fees"
            :key="feeset_fee.seq ? `${feeset_fee.seq}` : `create-${idx}`"
          >
            <td :class="$style.wide">
              <select v-model="feeset_fee.asset_seq" :disabled="feeset_fee.seq">
                <option :value="undefined">{{
                  $t('pg_feeset_info.choice')
                }}</option>
                <option v-for="val in assets" :key="val.seq" :value="val.seq">
                  {{ val.title }}
                </option>
              </select>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <input
                v-model="feeset_fee.fee_rate"
                type="text"
                :class="[$style.inputTypeText, $style.w120, $style.tRight]"
              />
              %
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.black, $style.button]"
                @click.prevent="handleSubmitFee(idx)"
                ><span>{{
                  feeset_fee.seq ? $t('admin.modify') : $t('admin.enroll')
                }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteFee(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!feeset_fees.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('pg_feeset_info.msg_no_registered')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.rowTable th,
.rowTable td,
.colTable th,
.colTable td {
  vertical-align: middle;
}

table div.infoText {
  margin-bottom: 0;
}
table div.infoText p.warning {
  margin: 0;
}
</style>
